import { useEffect, useMemo, useState } from "react";
import { tenantService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { ShieldQuestion } from "lucide-react";

export function useTenantScope() {
  const { user } = useAuth();
  const isSuper = user?.isSuperAdmin;
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  useEffect(() => {
    if (isSuper) tenantService.list().then((d) => setTenants(d.items || [])).catch(() => {});
  }, [isSuper]);
  const params = useMemo(() => (tenantId ? { tenantId } : {}), [tenantId]);
  return { isSuper, tenants, tenantId, setTenantId, params };
}

export function TenantPicker({ isSuper, tenants, tenantId, setTenantId }) {
  if (!isSuper) return null;
  return (
    <select value={tenantId} onChange={(e) => setTenantId(e.target.value)}
      className="bg-background border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
      data-testid="compliance-tenant-select">
      <option value="">Select tenant…</option>
      {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
    </select>
  );
}

export function SelectTenantNotice({ msg = "Choose a tenant above to view its compliance data." }) {
  return (
    <div className="bg-card border border-border rounded-xl p-12 text-center" data-testid="select-tenant-notice">
      <ShieldQuestion className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
      <h3 className="text-base font-semibold text-foreground">Select a tenant</h3>
      <p className="text-sm text-muted-foreground mt-1">{msg}</p>
    </div>
  );
}

export function PageHeader({ icon: Icon, title, subtitle, children }) {
  return (
    <header className="flex items-start justify-between gap-4 mb-6 flex-wrap">
      <div className="flex items-center gap-3">
        <span className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
          {Icon && <Icon className="h-5 w-5" />}
        </span>
        <div>
          <h1 className="text-xl font-bold text-foreground">{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2">{children}</div>
    </header>
  );
}

const PURPOSE_STYLE = {
  granted: "text-emerald-600 bg-emerald-500/10 border-emerald-500/30",
  withdrawn: "text-red-600 bg-red-500/10 border-red-500/30",
};
const STATUS_STYLE = {
  Submitted: "text-sky-600 bg-sky-500/10 border-sky-500/30",
  "In Review": "text-violet-600 bg-violet-500/10 border-violet-500/30",
  "In Progress": "text-amber-600 bg-amber-500/10 border-amber-500/30",
  Completed: "text-emerald-600 bg-emerald-500/10 border-emerald-500/30",
  Rejected: "text-red-600 bg-red-500/10 border-red-500/30",
};
const CLASS_STYLE = {
  PHI: "text-rose-600 bg-rose-500/10 border-rose-500/30",
  PII: "text-amber-600 bg-amber-500/10 border-amber-500/30",
  Sensitive: "text-violet-600 bg-violet-500/10 border-violet-500/30",
  Internal: "text-sky-600 bg-sky-500/10 border-sky-500/30",
  Public: "text-muted-foreground bg-muted/40 border-border",
};
export const STYLE_MAPS = { purpose: PURPOSE_STYLE, status: STATUS_STYLE, classification: CLASS_STYLE };

export function Badge({ label, kind, testid }) {
  const cls = (STYLE_MAPS[kind] || {})[label] || "text-muted-foreground bg-muted/40 border-border";
  return (
    <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border ${cls}`} data-testid={testid}>
      {String(label ?? "—")}
    </span>
  );
}

export const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return s; }
};

export const DSAR_TYPES = ["access", "export", "portability", "erasure", "rectification", "restriction", "objection"];
export const DSAR_STATUSES = ["Submitted", "In Review", "In Progress", "Completed", "Rejected"];
export const CONSENT_PURPOSES = ["email", "sms", "whatsapp", "call", "marketing", "data_processing"];
export const LEGAL_BASES = ["consent", "contract", "legal_obligation", "vital_interest", "public_task", "legitimate_interest"];
